import {
  BooleanInput,
  Datagrid,
  DateField,
  FunctionField,
  List,
  ListActions,
  NumberField,
  ReferenceField,
  ReferenceInput,
  SelectField,
  SelectInput,
  SimpleList,
  TextField,
} from "react-admin";
import { useMediaQuery } from "@mui/material";
import { settings } from "app";
import { dormitoryRequests } from "api/dormitoryApi";
import { MobileListRow } from "features/MobileListRow";
import {
  DormitoryAccommodationPaymentReportingStateChoices,
  DormitoryAccommodationPaymentStatusChoices,
  DormitoryManualPaymentMethodChoices,
} from "utils/choices";
import { Currency, MOBILE_BREAKPOINT } from "utils/constants";
import DatagridActions from "../Partials/DatagridActions";
import DormitoryAutocompleteInput from "../Partials/DormitoryAutocompleteInput";
import AccommodationPaymentActions from "./Actions";
import ReviewFlagField from "./ReviewFlagField";

const currencyOptions = { style: "currency", currency: Currency.EUR, minimumFractionDigits: 2 };

const listFilters = [
  <ReferenceInput
    key="accommodationId"
    source="accommodationId"
    reference={dormitoryRequests.getAccommodationsSelect()}
    alwaysOn
  >
    <DormitoryAutocompleteInput label="Студент" emptyText="Всички" matchSuggestion={() => true} />
  </ReferenceInput>,
  <SelectInput
    source="status"
    label="Статус"
    choices={DormitoryAccommodationPaymentStatusChoices}
    alwaysOn
    emptyText="Всички"
  />,
  <BooleanInput source="refundMarked" label="Само с отбелязано връщане" />,
];

const ListAccommodationPayments = () => {
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT);

  return (
    <List
      title="Плащания"
      exporter={false}
      filters={listFilters}
      actions={<ListActions hasCreate={false} />}
    >
      {isMobile ? (
        <SimpleList
          linkType={false}
          sx={settings.style.mobileList}
          primaryText={() => (
            <MobileListRow
              fields={[
                {
                  label: "Студент",
                  value: (
                    <ReferenceField
                      source="accommodationId"
                      reference={dormitoryRequests.getAccommodationsSelect()}
                      link={false}
                    >
                      <TextField source="name" />
                    </ReferenceField>
                  ),
                },
              ]}
            />
          )}
          secondaryText={() => (
            <div>
              <MobileListRow
                fields={[
                  { label: "Описание", value: <TextField source="articleName" /> },
                  { label: "Сума", value: <NumberField source="amount" options={currencyOptions} /> },
                ]}
              />
              <MobileListRow
                fields={[
                  {
                    label: "Статус",
                    value: <SelectField source="status" choices={DormitoryAccommodationPaymentStatusChoices} />,
                  },
                  {
                    label: "Отчитане",
                    value: (
                      <SelectField
                        source="reportingState"
                        choices={DormitoryAccommodationPaymentReportingStateChoices}
                      />
                    ),
                  },
                ]}
              />
              <MobileListRow fields={[{ label: "За проверка", value: <ReviewFlagField /> }]} />
              <MobileListRow
                fields={[
                  {
                    label: "Платено на",
                    value: (
                      <DateField
                        source="paidAt"
                        showTime
                        locales="bg-BG"
                      />
                    ),
                  },
                ]}
              />
              <MobileListRow
                fields={[
                  {
                    label: "Ръчно записано",
                    value: (
                      <SelectField
                        source="manualPaymentMethod"
                        choices={DormitoryManualPaymentMethodChoices}
                        emptyText="—"
                      />
                    ),
                  },
                ]}
              />
              <MobileListRow
                fields={[
                  {
                    label: "Референция",
                    value: <TextField source="manualPaymentReference" sx={{ overflowWrap: "anywhere" }} />,
                  },
                ]}
              />
              <MobileListRow fields={[{ label: "Задължение №", value: <TextField source="debtId" emptyText="—" /> }]} />
              <MobileListRow
                fields={[
                  {
                    label: "Върната сума",
                    value: <NumberField source="refundAmount" options={currencyOptions} />,
                  },
                ]}
              />
              <MobileListRow
                fields={[
                  {
                    label: "Отбелязано на",
                    value: (
                      <DateField
                        source="refundMarkedAt"
                        showTime
                        locales="bg-BG"
                      />
                    ),
                  },
                ]}
              />
              <MobileListRow
                fields={[
                  {
                    label: "Бележка",
                    value: <TextField source="refundNote" sx={{ overflowWrap: "anywhere" }} />,
                  },
                ]}
              />
            </div>
          )}
          tertiaryText={() => <AccommodationPaymentActions mobile />}
        />
      ) : (
        <Datagrid
          bulkActionButtons={false}
          rowClick={false}
          sx={{ "& .RaDatagrid-headerCell, & .RaDatagrid-rowCell": { textAlign: "center" } }}
        >
          <ReferenceField
            source="accommodationId"
            reference={dormitoryRequests.getAccommodationsSelect()}
            label="Студент"
            link={false}
            sortable={false}
          >
            <TextField source="name" />
          </ReferenceField>
          <TextField source="articleName" label="Описание" sortable={false} />
          <NumberField source="amount" label="Сума" options={currencyOptions} sortable={false} />
          <SelectField
            source="status"
            label="Статус"
            choices={DormitoryAccommodationPaymentStatusChoices}
            sortable={false}
          />
          <SelectField
            source="reportingState"
            label="Отчитане"
            choices={DormitoryAccommodationPaymentReportingStateChoices}
            sortable={false}
          />
          <FunctionField
            source="requiresReview"
            label="За проверка"
            sortable={false}
            render={() => <ReviewFlagField />}
          />
          <DateField
            source="paidAt"
            label="Платено на"
            sortable={false}
            showTime
            locales="bg-BG"
          />
          <SelectField
            source="manualPaymentMethod"
            label="Ръчно записано"
            choices={DormitoryManualPaymentMethodChoices}
            sortable={false}
            emptyText="—"
          />
          <TextField source="manualPaymentReference" label="Референция" sortable={false} />
          <TextField source="debtId" label="Задължение №" sortable={false} emptyText="—" />
          <NumberField source="refundAmount" label="Върната сума" options={currencyOptions} sortable={false} />
          <DateField
            source="refundMarkedAt"
            label="Отбелязано на"
            sortable={false}
            showTime
            locales="bg-BG"
          />
          <TextField source="refundNote" label="Бележка" sortable={false} />
          <FunctionField
            source="actions"
            label={false}
            sortable={false}
            render={() => (
              <DatagridActions>
                <AccommodationPaymentActions />
              </DatagridActions>
            )}
          />
        </Datagrid>
      )}
    </List>
  );
};

export default ListAccommodationPayments;
